import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '@shared/services/auth.service';
import { Observable, filter, map, switchMap, take, from } from 'rxjs';

export const roleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state
): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const expectedRoles = route.data['roles'] as Array<string>;
  const blockedRoles = route.data['blockedRoles'] as Array<string>;

  // 0. Check Login
  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/auth/login'], { queryParams: { returnUrl: state.url } });
  }

  // 1. Check Blocked Roles (Negative Check)
  // If user HAS a blocked role (and is NOT admin), deny access.
  if (blockedRoles?.length) {
    const isBlocked = blockedRoles.some(role => authService.hasRole(role));
    if (isBlocked && !authService.isAdmin()) {
      // Course exception: allow student access to case-editor when coming from a course flow.
      const isCourseEditorRoute = state.url.startsWith('/case-editor/') && state.url.includes('course=1');
      if (isCourseEditorRoute) {
        return true;
      }
      // Redirect to /pilot as the default fallback for blocked students
      return router.createUrlTree(['/pilot']);
    }
  }

  // 2. Check Allowed Roles (Positive Check)
  if (expectedRoles?.length) {
    // Return true if user has ANY of the expected roles
    return expectedRoles.some(role => authService.hasRole(role));
  }

  // 3. Default Allow
  // If no roles defined (and not blocked), allow.
  return true;
};
